import { useEffect, useState } from "react";
import DecorativeEllipse from "@/components/DecorativeEllipse";

const includedItems = [
  "5 módulos en video (6–12 min cada uno)",
  "5 guías descargables (PDF)",
  "5 Ebooks complementarios (PDF)",
  "Plantillas Excel / Sheets listas para usar",
  "Checklists de ejecución por módulo",
  "Bonus 1: Banco de mensajes copiar/pegar",
  "Bonus 2: Lista de 30 ideas de oportunidades",
];

const STORAGE_KEY = "mujer-abundante-timer-expires";
const TOTAL_SECONDS = 4 * 60;

export default function CheckoutPage() {
  const [timeLeft, setTimeLeft] = useState(TOTAL_SECONDS);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const now = Date.now();

    let targetTime: number;
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed > now) {
        targetTime = parsed;
      } else {
        setExpired(true);
        setTimeLeft(0);
        return;
      }
    } else {
      targetTime = now + TOTAL_SECONDS * 1000;
      sessionStorage.setItem(STORAGE_KEY, String(targetTime));
    }

    const update = () => {
      const diff = Math.max(0, Math.ceil((targetTime - Date.now()) / 1000));
      setTimeLeft(diff);
      if (diff <= 0) {
        setExpired(true);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  const m = Math.floor(timeLeft / 60);
  const s = timeLeft % 60;

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: "#0D0D0D" }}>
      <DecorativeEllipse className="-top-32 -left-32" size={500} opacity={0.06} blur={140} />
      <DecorativeEllipse className="-bottom-40 -right-40" size={600} opacity={0.05} blur={160} />
      <DecorativeEllipse className="top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2" size={700} opacity={0.03} blur={200} />
      <div className="absolute inset-0 bg-gradient-to-b from-gold/[0.02] via-transparent to-gold/[0.03] pointer-events-none" />

      {/* Top bar */}
      <div className={`w-full border-b ${expired ? "border-red-900/40" : "border-gold/20"}`} style={{ backgroundColor: "#0A0A0A" }}>
        <div className="max-w-3xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 md:gap-6 flex-wrap">
          <div className="flex items-center gap-2 shrink-0">
            {!expired && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
            )}
            <span className={`font-sans text-[11px] md:text-xs font-semibold tracking-wide uppercase ${expired ? "text-red-400" : "text-gold"}`}>
              {expired ? "Tiempo agotado — precio ahora $47" : "Oferta especial por tiempo limitado"}
            </span>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2">
            <div className="flex items-baseline gap-0.5">
              <span className="font-sans text-lg md:text-xl font-bold tabular-nums text-gold">{String(m).padStart(2, "0")}</span>
              <span className="font-sans text-[9px] md:text-[10px] text-cream/40 uppercase tracking-wider">min</span>
            </div>
            <span className="font-sans text-lg md:text-xl font-light text-gold/70 -mt-0.5">:</span>
            <div className="flex items-baseline gap-0.5">
              <span className="font-sans text-lg md:text-xl font-bold tabular-nums text-gold">{String(s).padStart(2, "0")}</span>
              <span className="font-sans text-[9px] md:text-[10px] text-cream/40 uppercase tracking-wider">seg</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main */}
      <main className="relative z-10 max-w-xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-16">
        {/* Headlines */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 rounded-full border border-gold/40 text-gold text-xs md:text-sm font-sans font-medium tracking-wider uppercase mb-5">
            Precio de lanzamiento — Solo para miembros
          </span>

          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-cream leading-tight mb-4">
            Tu acceso está a <span className="text-gold">un clic</span> de distancia
          </h1>
          <p className="font-sans text-base md:text-lg text-cream/70 max-w-md mx-auto leading-relaxed">
            No necesitas toda la info. Ya la viste. Esto es lo único que separa tu resultado de tu decisión.
          </p>
        </div>

        {/* Timer card */}
        <div className={`w-full rounded-lg border-2 px-4 py-5 md:px-6 md:py-6 text-center mb-8 ${expired ? "border-red-500/50 bg-red-950/10" : "border-gold/50 bg-[#0A0A0A] animate-pulse-glow"}`}>
          <div className="flex items-center justify-center gap-2 mb-4">
            {!expired && (
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
              </span>
            )}
            <span className={`font-sans text-sm md:text-base font-bold tracking-wide uppercase ${expired ? "text-red-400" : "text-gold"}`}>
              {expired ? "Tiempo agotado — precio ahora $47" : "Oferta especial por tiempo limitado"}
            </span>
            {!expired && (
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
              </span>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 md:gap-4 mb-3">
            <div className="flex flex-col items-center">
              <span className={`font-sans text-4xl sm:text-5xl md:text-6xl font-bold tabular-nums leading-none ${expired ? "text-cream/20" : "text-gold"}`}>
                {String(m).padStart(2, "0")}
              </span>
              <span className="font-sans text-[10px] md:text-xs text-cream/40 mt-1 uppercase tracking-[0.15em]">min</span>
            </div>
            <span className={`font-sans text-3xl sm:text-4xl md:text-5xl font-light -mt-2 ${expired ? "text-cream/10" : "text-gold/60"}`}>:</span>
            <div className="flex flex-col items-center">
              <span className={`font-sans text-4xl sm:text-5xl md:text-6xl font-bold tabular-nums leading-none ${expired ? "text-cream/20" : "text-gold"}`}>
                {String(s).padStart(2, "0")}
              </span>
              <span className="font-sans text-[10px] md:text-xs text-cream/40 mt-1 uppercase tracking-[0.15em]">seg</span>
            </div>
          </div>

          {!expired && (
            <p className="font-sans text-xs md:text-sm text-cream/50 tracking-wide">El precio subirá al finalizar el contador</p>
          )}
        </div>

        {/* Included */}
        <div className="bg-[#141414] rounded-lg border border-gold/20 p-6 md:p-8 mb-8">
          <h2 className="font-serif text-xl md:text-2xl text-cream mb-5 text-center">Todo lo que recibís hoy</h2>
          <ul className="space-y-3">
            {includedItems.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-gold text-sm mt-0.5">&#10022;</span>
                <span className="font-sans text-sm md:text-base text-cream/90">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Price + CTA */}
        <div className="bg-[#141414] rounded-lg border border-gold/40 p-6 md:p-10 text-center mb-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gold/[0.04] via-transparent to-gold/[0.02] pointer-events-none" />
          <div className="relative z-10">
            <p className="font-sans text-sm text-cream/40 mb-1">
              <span className="line-through">Valor real: USD $47</span>
            </p>
            <div className="mb-2">
              <span className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-gold">$15</span>
              <span className="font-sans text-sm md:text-base text-cream/50 ml-2">USD</span>
            </div>
            <p className="font-sans text-xs md:text-sm text-cream/40 mb-6">Pago único. Acceso de por vida.</p>

            <a
              href="https://buy.stripe.com/eVqfZa95e2QQ51B8uCd3i05"
              className="block w-full py-4 md:py-5 rounded-md font-sans font-semibold text-base md:text-lg text-white transition-all duration-300 hover:scale-[1.01] hover:brightness-110"
              style={{ backgroundColor: "#2E7D32" }}
            >
              <span>Quiero acceso ahora</span>
              <i className="ri-arrow-right-line text-lg ml-2" />
            </a>

            <div className="mt-5 flex items-center justify-center gap-2">
              <i className="ri-shield-check-line text-gold" />
              <span className="font-sans text-xs md:text-sm text-cream/50">Garantía de 7 días. Sin preguntas.</span>
            </div>
          </div>
        </div>

        {/* Trust line */}
        <div className="text-center">
          <p className="font-sans text-xs text-cream/30 mb-1">Pagá con total seguridad</p>
          <div className="flex items-center justify-center gap-4 text-cream/20">
            <i className="ri-visa-line text-2xl" />
            <i className="ri-mastercard-line text-2xl" />
            <i className="ri-secure-payment-line text-2xl" />
          </div>
        </div>
      </main>
    </div>
  );
}